// ----------------------------------------------------------------------------
// Copyright (c) Ben Coleman, 2020
// Licensed under the MIT License.
//
// Set of methods to call the Graph API, using REST and fetch
// ----------------------------------------------------------------------------

import axios from "axios";
import auth from "./auth";
import { store } from "@/vuex/store.js";

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";
const GRAPH_SCOPES = [
  "user.readwrite.all",
  "sites.readwrite.all",
  "calendars.readwrite",
  "files.readwrite.all",
  "directory.readwrite.all",
  "directory.read.all",
  "group.readwrite.all",
  "group.read.all",
  "sites.fullcontrol.all",
  "people.read"
];

let accessToken;

export default {
  async deleteMessage(messageId) {
    const accessToken = store.state.aToken;
    const deleteUrl = `/me/messages/${messageId}`;

    const response = await fetch(`${GRAPH_BASE}${deleteUrl}`, {
      method: "DELETE",
      headers: {
        authorization: `bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete message: ${response.statusText}`);
    }

    // Return true if successfully deleted (no content response, status 204)
    return response.status === 204;
  },

  async sendDraftEmail(draftMessageId) {
    const accessToken = store.state.aToken;

    const GRAPH_BASE_URL = "https://graph.microsoft.com/v1.0";

    // Construct the URL for sending the draft email
    const url = `${GRAPH_BASE_URL}/me/messages/${draftMessageId}/send`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete message: ${response.statusText}`);
    }

    // Return true if successfully deleted (no content response, status 204)
    return response.status === 204;
  },

  async removeAttachmentFromEmail(messageId, attachmentId) {
    const accessToken = store.state.aToken;
    const deleteAttachmentUrl = `/me/messages/${messageId}/attachments/${attachmentId}`;

    const response = await fetch(`${GRAPH_BASE}${deleteAttachmentUrl}`, {
      method: "DELETE",
      headers: {
        authorization: `bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to remove attachment: ${response.statusText}`);
    }

    // Return true if the attachment was successfully removed (no content response, status 204)
    return response.status === 204;
  },

  async updateMessage(updatedFields) {
    const accessToken = store.state.aToken;
    const updateUrl = `/me/messages/${updatedFields["id"]}`;

    const response = await fetch(`${GRAPH_BASE}${updateUrl}`, {
      method: "PATCH",
      headers: {
        authorization: `bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updatedFields), // Pass an object with the fields you want to update
    });

    if (!response.ok) {
      console.log(await response.text());
      throw new Error(`Failed to update message: ${response.statusText}`);
    }

    return await response.json(); // The updated message object
  },
  async createReplyDraft(messageId) {
    const createReplyAllUrl = `/me/messages/${messageId}/createReplyAll`;

    const accessToken = store.state.aToken;

    const response = await fetch(`${GRAPH_BASE}${createReplyAllUrl}`, {
      method: "POST",
      headers: {
        authorization: `bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to create reply-all draft: ${response.statusText}`
      );
    }

    return await response.json(); // The draft message object
  },

  async createReplyAllDraft(messageId) {
    const createReplyAllUrl = `/me/messages/${messageId}/createReplyAll`;

    const accessToken = store.state.aToken;

    const response = await fetch(`${GRAPH_BASE}${createReplyAllUrl}`, {
      method: "POST",
      headers: {
        authorization: `bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to create reply-all draft: ${response.statusText}`
      );
    }

    return await response.json(); // The draft message object
  },

  async createFowardEmail(messageId) {
    const createFoward = `/me/messages/${messageId}/createForward`;

    const accessToken = store.state.aToken;

    const response = await fetch(`${GRAPH_BASE}${createFoward}`, {
      method: "POST",
      headers: {
        authorization: `bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to create reply-all draft: ${response.statusText}`
      );
    }

    return await response.json(); // The draft message object
  },

  async getInboxEmails(next_link, filter, folderName) {
    let resp = await callGraph(
      next_link == null
        ? "/me/mailFolders/" +
            folderName +
            "/messages/?" +
            filter +
            "&$select=sender,subject,createdDateTime,hasAttachments,isRead"
        : next_link,
      next_link == null
    );
    if (resp) {
      let data = await resp.json();
      // console.log("Got data ", data)
      return data;
    }

    return data;
  },
  async getMixedEmails(next_link, filter) {
    let response = await callGraph(
      next_link == null
        ? "/me/messages/?" +
            filter +
            "&$select=sender,subject,createdDateTime,hasAttachments,isDraft,isRead"
        : next_link,
      next_link == null
    );
    if (!response.ok) {
      throw new Error(`Failed to create draft message: ${response.statusText}`);
    }

    return await response.json();
  },

  async createDraftMessage(final_object) {
    const createMessageUrl = `/me/messages`;

    const accessToken = store.state.aToken;

    // const bodyContent = {
    //   subject: final_object.subject,
    //   sender: {
    //     emailAddress: {
    //       address: store.state.logged_in_user.tenance_email,
    //     },
    //   },
    //   body: {
    //     contentType: "HTML",
    //     content: final_object.body,
    //   },
    //   toRecipients: final_object.recipients,
    //   //   toRecipients: recipients.map(email => ({
    //   //     emailAddress: { address: email },
    //   //   })),
    // };

    const response = await fetch(`${GRAPH_BASE}${createMessageUrl}`, {
      method: "POST",
      headers: {
        authorization: `bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(final_object),
    });

    if (!response.ok) {
      throw new Error(`Failed to create draft message: ${response.statusText}`);
    }

    return await response.json(); // The created draft message object
  },

  async addAttachmentToDraftMessage(
    messageId,
    attachmentName,
    attachmentContent
  ) {
    const addAttachmentUrl = `/me/messages/${messageId}/attachments?@microsoft.graph.conflictBehavior=rename`;

    const accessToken = store.state.aToken;

    const bodyContent = {
      "@odata.type": "#microsoft.graph.fileAttachment",
      name: attachmentName,
      contentBytes: (attachmentContent), // Base64 encode the file content
    };

    const response = await fetch(`${GRAPH_BASE}${addAttachmentUrl}`, {
      method: "POST",
      headers: {
        authorization: `bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(bodyContent),
    });

    if (!response.ok) {
      throw new Error(`Failed to add attachment: ${response.statusText}`);
    }

    return await response.json(); // The added attachment object
  },
};

//
// Common fetch wrapper (private)
//
async function callGraph(apiPath, no_base = true) {
  // Acquire an access token to call APIs (like Graph)
  // Safe to call repeatedly as MSAL caches tokens locally
  accessToken = store.state.aToken;

  let resp = await fetch(`${no_base ? GRAPH_BASE : ""}${apiPath}`, {
    headers: { authorization: `bearer ${accessToken}` },
  });

  if (!resp.ok) {
    throw new Error(
      `Call to ${GRAPH_BASE}${apiPath} failed: ${resp.statusText}`
    );
  }

  return resp;
}
